import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  CButton,
  CCard,
  CCardBody,
  CCardGroup,
  CCol,
  CImage,
  CRow,
  CSpinner,
  CBadge,
  CNav,
  CNavItem,
  CNavLink,
  CTabContent,
  CTabPane,
} from "@coreui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faEnvelope, faEye, faGraduationCap, faBriefcase, faHeart } from "@fortawesome/free-solid-svg-icons";
import { useTranslationContext } from "@/context/TranslationContext";
import { dummyUser } from "@/constants/translation/dummyUser.translation";
import { breadcrumb_key } from "@/constants/translation/common.translation";
import { dummyUser as dummyUserRoutes } from "@/libs/routes";
import { useGetDummyUser } from "@/hooks/useDummyUser";
import { setFormatDate, dateTimeFormat } from "@/utils/dateFormate";
import avatar from "@/assets/images/avatars/DefaultImg.png";
import constants from "@/libs/constant";

const isOnlineValue = (value) => value === true || value === constants.status.apiStatus.true;

/**
 * Read-only detail view for a single Dummy User, styled to match the normal
 * User view page (profile header card + tabbed sections). Education, Work
 * Experience and Interests & Appearances are read from the same
 * userProfile* relations as a regular user, since dummy users share the
 * same User/UserProfile tables.
 * @returns {JSX.Element}
 */
const ViewDummyUser = () => {
  const t = useTranslationContext();
  const navigate = useNavigate();
  const { id } = useParams();

  const [activeTab, setActiveTab] = useState(constants.dummyUserTabs.PERSONAL);

  const {
    data: response,
    isLoading,
    error: dummyUserError,
  } = useGetDummyUser(id, null, (error) => {
    console.error("Error fetching dummy user details:", error);
  });

  const dummyUserData = response?.data;

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <CSpinner color="primary" />
      </div>
    );
  }

  if (dummyUserError || !dummyUserData) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <div className="text-center">
          <h4>{t(dummyUser.view.error.not_found)}</h4>
          <p>{t(dummyUser.view.error.not_found_description)}</p>
          <CButton color="primary" onClick={() => navigate(dummyUserRoutes.list)}>
            {t(dummyUser.view.error.back_to_list)}
          </CButton>
        </div>
      </div>
    );
  }

  const isOnline = isOnlineValue(dummyUserData.is_online);
  const firstName = dummyUserData.full_name?.split(" ")?.[0] || t(dummyUser.view.defaults.na);
  const lastName = dummyUserData.full_name?.split(" ")?.slice(1).join(" ") || t(dummyUser.view.defaults.na);

  return (
    <div className="view-user-container">
      {/* Profile Header Card */}
      <CCard className="mb-4 profile-header-card">
        <CCardBody>
          <CRow className="align-items-center">
            <CCol md="auto" className="text-center mb-3 mb-md-0">
              <CImage
                src={avatar}
                alt="profile_img"
                className="rounded-circle"
                style={{
                  width: "120px",
                  height: "120px",
                  objectFit: "cover",
                  border: "4px solid #212631",
                }}
              />
            </CCol>
            <CCol>
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
                <div>
                  <h2 className="mb-2">{dummyUserData.full_name || t(dummyUser.view.defaults.na)}</h2>
                  <div className="d-flex flex-wrap gap-3 mb-3">
                    <span className="text-muted">
                      <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                      {dummyUserData.email || t(dummyUser.view.defaults.na)}
                    </span>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    <CBadge color={isOnline ? "success" : "secondary"}>
                      {t(dummyUser.view.status_label)}: {isOnline ? t(dummyUser.list.status.online) : t(dummyUser.list.status.offline)}
                    </CBadge>
                  </div>
                </div>
              </div>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>

      {/* Navigation Tabs */}
      <CNav variant="tabs" className="mb-4 view-user-tabs">
        <CNavItem>
          <CNavLink
            href="#"
            active={activeTab === constants.dummyUserTabs.PERSONAL}
            onClick={(e) => { e.preventDefault(); setActiveTab(constants.dummyUserTabs.PERSONAL); }}
          >
            <FontAwesomeIcon icon={faUser} className="me-2" />
            {t(dummyUser.view.tabs.personal_info)}
          </CNavLink>
        </CNavItem>
        <CNavItem>
          <CNavLink
            href="#"
            active={activeTab === constants.dummyUserTabs.PROFILE}
            onClick={(e) => { e.preventDefault(); setActiveTab(constants.dummyUserTabs.PROFILE); }}
          >
            <FontAwesomeIcon icon={faEye} className="me-2" />
            {t(dummyUser.view.tabs.profile_details)}
          </CNavLink>
        </CNavItem>
        <CNavItem>
          <CNavLink
            href="#"
            active={activeTab === constants.dummyUserTabs.EDUCATION}
            onClick={(e) => { e.preventDefault(); setActiveTab(constants.dummyUserTabs.EDUCATION); }}
          >
            <FontAwesomeIcon icon={faGraduationCap} className="me-2" />
            {t(dummyUser.view.tabs.education)}
          </CNavLink>
        </CNavItem>
        <CNavItem>
          <CNavLink
            href="#"
            active={activeTab === constants.dummyUserTabs.WORK}
            onClick={(e) => { e.preventDefault(); setActiveTab(constants.dummyUserTabs.WORK); }}
          >
            <FontAwesomeIcon icon={faBriefcase} className="me-2" />
            {t(dummyUser.view.tabs.work_experience)}
          </CNavLink>
        </CNavItem>
        <CNavItem>
          <CNavLink
            href="#"
            active={activeTab === constants.dummyUserTabs.INTERESTS}
            onClick={(e) => { e.preventDefault(); setActiveTab(constants.dummyUserTabs.INTERESTS); }}
          >
            <FontAwesomeIcon icon={faHeart} className="me-2" />
            {t(dummyUser.view.tabs.interests_appearances)}
          </CNavLink>
        </CNavItem>
      </CNav>

      {/* Tab Content */}
      <CTabContent>
        {/* Personal Information Tab */}
        <CTabPane visible={activeTab === constants.dummyUserTabs.PERSONAL}>
          <CCard>
            <CCardBody>
              <h4 className="card-title mb-4">{t(dummyUser.view.personal_info.title)}</h4>
              <CRow>
                <CCol md={6} className="mb-3">
                  <strong>{t(dummyUser.view.first_name_label)}:</strong>
                  <p className="text-muted">{firstName}</p>
                </CCol>
                <CCol md={6} className="mb-3">
                  <strong>{t(dummyUser.view.last_name_label)}:</strong>
                  <p className="text-muted">{lastName}</p>
                </CCol>
                <CCol md={6} className="mb-3">
                  <strong>{t(dummyUser.view.email_label)}:</strong>
                  <p className="text-muted">{dummyUserData.email || t(dummyUser.view.defaults.na)}</p>
                </CCol>
                <CCol md={6} className="mb-3">
                  <strong>{t(dummyUser.view.status_label)}:</strong>
                  <p>
                    <CBadge color={isOnline ? "success" : "secondary"}>
                      {isOnline ? t(dummyUser.list.status.online) : t(dummyUser.list.status.offline)}
                    </CBadge>
                  </p>
                </CCol>
                <CCol md={6} className="mb-3">
                  <strong>{t(dummyUser.view.created_at_label)}:</strong>
                  <p className="text-muted">{dummyUserData.created_at ? setFormatDate(dummyUserData.created_at) : t(dummyUser.view.defaults.na)}</p>
                </CCol>
                <CCol md={6} className="mb-3">
                  <strong>{t(dummyUser.view.last_online_label)}:</strong>
                  <p className="text-muted">{dummyUserData.last_online_at ? dateTimeFormat(dummyUserData.last_online_at) : t(dummyUser.view.defaults.na)}</p>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </CTabPane>

        {/* Profile Details (Address) Tab */}
        <CTabPane visible={activeTab === constants.dummyUserTabs.PROFILE}>
          <CCard>
            <CCardBody>
              <h4 className="card-title mb-4">{t(dummyUser.view.profile_details.title)}</h4>
              <CRow>
                <CCol md={6} className="mb-3">
                  <strong>{t(dummyUser.view.bio_label)}:</strong>
                  <p className="text-muted">{dummyUserData.bio || t(dummyUser.view.defaults.na)}</p>
                </CCol>
                <CCol md={6} className="mb-3">
                  <strong>{t(dummyUser.view.about_me_label)}:</strong>
                  <p className="text-muted">{dummyUserData.about_me || t(dummyUser.view.defaults.na)}</p>
                </CCol>
                <CCol md={6} className="mb-3">
                  <strong>{t(dummyUser.view.date_of_birth_label)}:</strong>
                  <p className="text-muted">{dummyUserData.date_of_birth || t(dummyUser.view.defaults.na)}</p>
                </CCol>
                <CCol md={6} className="mb-3">
                  <strong>{t(dummyUser.view.gender_label)}:</strong>
                  <p className="text-muted">{dummyUserData.gender || t(dummyUser.view.defaults.na)}</p>
                </CCol>
                <CCol md={6} className="mb-3">
                  <strong>{t(dummyUser.view.house_number_label)}:</strong>
                  <p className="text-muted">{dummyUserData.house_number || t(dummyUser.view.defaults.na)}</p>
                </CCol>
                <CCol md={6} className="mb-3">
                  <strong>{t(dummyUser.view.street_label)}:</strong>
                  <p className="text-muted">{dummyUserData.street || t(dummyUser.view.defaults.na)}</p>
                </CCol>
                <CCol md={6} className="mb-3">
                  <strong>{t(dummyUser.view.city_label)}:</strong>
                  <p className="text-muted">{dummyUserData.city || t(dummyUser.view.defaults.na)}</p>
                </CCol>
                <CCol md={6} className="mb-3">
                  <strong>{t(dummyUser.view.country_label)}:</strong>
                  <p className="text-muted">{dummyUserData.country || t(dummyUser.view.defaults.na)}</p>
                </CCol>
                <CCol md={6} className="mb-3">
                  <strong>{t(dummyUser.view.zip_code_label)}:</strong>
                  <p className="text-muted">{dummyUserData.zip_code || t(dummyUser.view.defaults.na)}</p>
                </CCol>
                <CCol md={6} className="mb-3">
                  <strong>{t(dummyUser.view.preferred_language_label)}:</strong>
                  <p className="text-muted">{dummyUserData.preferred_language?.name || t(dummyUser.view.defaults.na)}</p>
                </CCol>
                <CCol md={6} className="mb-3">
                  <strong>{t(dummyUser.view.latitude_label)}:</strong>
                  <p className="text-muted">{dummyUserData.latitude ?? t(dummyUser.view.defaults.na)}</p>
                </CCol>
                <CCol md={6} className="mb-3">
                  <strong>{t(dummyUser.view.longitude_label)}:</strong>
                  <p className="text-muted">{dummyUserData.longitude ?? t(dummyUser.view.defaults.na)}</p>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </CTabPane>

        {/* Education Tab */}
        <CTabPane visible={activeTab === constants.dummyUserTabs.EDUCATION}>
          <CCard>
            <CCardBody>
              <h4 className="card-title mb-4">{t(dummyUser.view.education.title)}</h4>
              {dummyUserData.education && dummyUserData.education.length > 0 ? (
                dummyUserData.education.map((edu) => (
                  <CCard key={edu.uuid} className="mb-3">
                    <CCardBody>
                      <h6 className="card-subtitle mb-2 text-primary">
                        {edu.degree?.name || t(dummyUser.view.defaults.na)} in {edu.field_of_study?.name || t(dummyUser.view.defaults.na)}
                      </h6>
                      <p className="mb-1"><strong>{t(dummyUser.view.education.school)}:</strong> {edu.school || t(dummyUser.view.defaults.na)}</p>
                      <p className="mb-1"><strong>{t(dummyUser.view.education.graduation_year)}:</strong> {edu.graduation_year?.year || t(dummyUser.view.defaults.na)}</p>
                      <p className="mb-0">
                        <CBadge color={edu.is_primary ? "success" : "secondary"}>
                          {edu.is_primary ? t(dummyUser.view.education.primary) : t(dummyUser.view.education.secondary)}
                        </CBadge>
                      </p>
                    </CCardBody>
                  </CCard>
                ))
              ) : (
                <p className="text-muted">{t(dummyUser.view.education.no_education_info)}</p>
              )}
            </CCardBody>
          </CCard>
        </CTabPane>

        {/* Work Experience Tab */}
        <CTabPane visible={activeTab === constants.dummyUserTabs.WORK}>
          <CCard>
            <CCardBody>
              <h4 className="card-title mb-4">{t(dummyUser.view.work_experience.title)}</h4>
              {dummyUserData.jobs && dummyUserData.jobs.length > 0 ? (
                dummyUserData.jobs.map((job) => (
                  <CCard key={job.uuid} className="mb-3">
                    <CCardBody>
                      <h6 className="card-subtitle mb-2 text-primary">{job.title || t(dummyUser.view.defaults.na)}</h6>
                      <p className="mb-1"><strong>{t(dummyUser.view.work_experience.company)}:</strong> {job.company || t(dummyUser.view.defaults.na)}</p>
                      <p className="mb-1"><strong>{t(dummyUser.view.work_experience.industry)}:</strong> {job.industry?.name || t(dummyUser.view.defaults.na)}</p>
                      <p className="mb-0"><strong>{t(dummyUser.view.work_experience.employment_type)}:</strong> {job.employment_type?.name || t(dummyUser.view.defaults.na)}</p>
                    </CCardBody>
                  </CCard>
                ))
              ) : (
                <p className="text-muted">{t(dummyUser.view.work_experience.no_work_info)}</p>
              )}
            </CCardBody>
          </CCard>
        </CTabPane>

        {/* Interests & Appearances Tab */}
        <CTabPane visible={activeTab === constants.dummyUserTabs.INTERESTS}>
          <CCardGroup>
            <CCard>
              <CCardBody>
                <h4 className="card-title mb-4">{t(dummyUser.view.interests_appearances.interests_title)}</h4>
                {dummyUserData.interests && dummyUserData.interests.length > 0 ? (
                  <div className="d-flex flex-wrap gap-2">
                    {dummyUserData.interests.map((interest) => (
                      <CBadge key={interest.uuid} color="info" className="p-2">
                        {interest.name || t(dummyUser.view.defaults.na)}
                      </CBadge>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted">{t(dummyUser.view.interests_appearances.no_interests)}</p>
                )}
              </CCardBody>
            </CCard>
            <CCard>
              <CCardBody>
                <h4 className="card-title mb-4">{t(dummyUser.view.interests_appearances.appearances_title)}</h4>
                {dummyUserData.appearances && dummyUserData.appearances.length > 0 ? (
                  <div className="d-flex flex-wrap gap-2">
                    {dummyUserData.appearances.map((appearance) => (
                      <CBadge key={appearance.uuid} color="secondary" className="p-2">
                        {appearance.name || t(dummyUser.view.defaults.na)} ({appearance.type || t(dummyUser.view.defaults.na)})
                      </CBadge>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted">{t(dummyUser.view.interests_appearances.no_appearances)}</p>
                )}
              </CCardBody>
            </CCard>
          </CCardGroup>
        </CTabPane>
      </CTabContent>
    </div>
  );
};

// Static breadcrumb
ViewDummyUser.breadCrumbArray = [
  {
    name: breadcrumb_key.dummyUser?.list,
    pathname: dummyUserRoutes.list,
    active: false,
  },
  {
    name: breadcrumb_key.dummyUser?.view,
    pathname: "",
    active: true,
  },
];

export default ViewDummyUser;
